import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import SqrtInput, {
  getSqrtInputNumericValue,
} from "../../../../components/inputs/SqrtInput";

const CASES = [
  { ab: 5, ac: 2, numerator: 3, denominator: 5 },
  { ab: 6, ac: 5, numerator: 1, denominator: 2 },
  { ab: 8, ac: 5, numerator: 3, denominator: 5 },
  { ab: 7, ac: 5, numerator: 4, denominator: 5 },
  { ab: 9, ac: 5, numerator: 1, denominator: 3 },
  { ab: 8, ac: 3, numerator: 2, denominator: 3 },
  { ab: 12, ac: 5, numerator: 3, denominator: 5 },
  { ab: 6, ac: 4, numerator: 1, denominator: 2 },
];

const pick = (items) => items[Math.floor(Math.random() * items.length)];

function simplifySquareRoot(n) {
  let outside = 1;
  let inside = n;
  for (let factor = 2; factor * factor <= inside; factor += 1) {
    while (inside % (factor * factor) === 0) {
      outside *= factor;
      inside /= factor * factor;
    }
  }
  return { outside, inside };
}

function rootLatex(n) {
  const { outside, inside } = simplifySquareRoot(n);
  if (inside === 1) return String(outside);
  return `${outside === 1 ? "" : outside}\\sqrt{${inside}}`;
}

function buildData(values) {
  const { ab, ac, numerator, denominator } = values;
  const subtracted = (2 * ab * ac * numerator) / denominator;
  const radicand = ab ** 2 + ac ** 2 - subtracted;
  return { ab, ac, numerator, denominator, subtracted, radicand };
}

export function generateTask() {
  return {
    type: "FindSideFromTwoSidesAndCosineTask",
    data: buildData(pick(CASES)),
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Dany jest trójkąt <InlineMath math="ABC" />, w którym{" "}
        <InlineMath math={`|AB|=${data.ab}`} />,{" "}
        <InlineMath math={`|AC|=${data.ac}`} /> oraz{" "}
        <InlineMath math={`\\cos |\\angle BAC|=\\frac{${data.numerator}}{${data.denominator}}`} />.
      </p>
      <p>Oblicz długość boku <InlineMath math="BC" />.</p>
    </div>
  );
}

export function renderInput(_task, value, onChange, disabled) {
  return (
    <SqrtInput
      value={value}
      onChange={onChange}
      disabled={disabled}
      label="|BC|"
    />
  );
}

export function checkAnswer({ data }, value) {
  const parsed = getSqrtInputNumericValue(value);
  return Number.isFinite(parsed) && Math.abs(parsed - Math.sqrt(data.radicand)) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`|BC|=${rootLatex(data.radicand)}`} />;
}

export function renderUserAnswer(value) {
  const raw = String(value?.raw ?? value ?? "").trim();
  if (!raw) return <i>(brak odpowiedzi)</i>;
  const latex = raw
    .replace(/sqrt\s*\(?\s*(\d+)\s*\)?/gi, "\\sqrt{$1}")
    .replace(/(\d*)√(\d+)/g, (_match, coefficient, radicand) => `${coefficient}\\sqrt{${radicand}}`);
  return <InlineMath math={`|BC|=${latex}`} />;
}

export function renderExplanation({ data }) {
  const sum = data.ab ** 2 + data.ac ** 2;
  return (
    <div style={{ textAlign: "left" }}>
      <p><strong>Krok 1.</strong> Korzystamy z twierdzenia cosinusów.</p>
      <BlockMath math={String.raw`|BC|^2=|AB|^2+|AC|^2-2\cdot |AB|\cdot |AC|\cdot \cos |\angle BAC|`} />
      <p><strong>Krok 2.</strong> Podstawiamy dane i upraszczamy ułamek.</p>
      <BlockMath math={`|BC|^2=${data.ab}^2+${data.ac}^2-2\\cdot ${data.ab}\\cdot ${data.ac}\\cdot \\frac{${data.numerator}}{${data.denominator}}`} />
      <BlockMath math={`|BC|^2=${sum}-${data.subtracted}=${data.radicand}`} />
      <p><strong>Krok 3.</strong> Długość jest dodatnia, więc wybieramy dodatni pierwiastek.</p>
      <BlockMath math={`|BC|=${rootLatex(data.radicand)}`} />
    </div>
  );
}

export const cosineGivenValueCases = CASES;
export { buildData, rootLatex };
